import type { ChangeEvent, FocusEvent, FunctionComponent, KeyboardEvent } from 'react';
import classNames from 'classnames';
import { Button, Spinner } from '@fff-web/fff-ui-shared';
import { ICoupon, IGiftCard } from '../../../interfaces/coupon';
import { CTA_APPLY_COUPON } from '../../utils/buttonCTA';
import { generateCouponText } from '../../utils/generateCouponText';
import {
  CI_FORM,
  CI_INPUT,
  CI_INPUT_WRAPPER,
  CI_LABEL,
} from '../Recurly/CouponInput/couponInputTestIds';
import { FieldError } from '../Shared';
import * as styles from './UniversalCouponInput.module.css';

const PLACEHOLDER = 'Enter Promo Code or Gift Card';
const LABEL = 'Enter Promo Code';

interface UniversalCouponInputProps {
  coupon: string;
  handleOnChange: (e: ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  label?: string;
  error: boolean | null;
  loading: boolean;
  handleCallCouponApi: () => void;
  variant?: 'default' | 'secondary';
  couponData: IGiftCard | ICoupon | null;
  discount?: string | number;
}
const DEFAULT_DISCOUNT = '0.00';

const UniversalCouponInput: FunctionComponent<UniversalCouponInputProps> = ({
  coupon,
  handleOnChange,
  placeholder,
  error,
  loading,
  label,
  handleCallCouponApi,
  couponData,
  variant = 'default',
  discount,
}) => {
  const isDefault = variant === 'default';
  const invalidInputStyles = error ? ` ${styles.couponInputInvalid}` : '';
  const shouldShowCouponText = !isDefault && !error && couponData;

  const handleInputBlur = (e: FocusEvent<HTMLInputElement>) => {
    e.preventDefault();
    handleCallCouponApi();
  };

  const handleSubmit = () => {
    handleCallCouponApi();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCallCouponApi();
    }
  };

  return (
    <div data-testid={CI_FORM} className={styles.couponInputContainer}>
      <label data-testid={CI_LABEL} htmlFor="couponCode" className={styles.couponInputLabel}>
        <div className={styles.discounAndLabel}>
          {label ?? LABEL} <p>{(!isDefault && discount) || DEFAULT_DISCOUNT}</p>
        </div>
        <div
          data-testid={CI_INPUT_WRAPPER}
          className={classNames({
            [styles.defaultInputWrapper]: isDefault,
            [styles.secondaryInputWrapper]: !isDefault,
          })}
        >
          <input
            data-testid={CI_INPUT}
            className={`${styles.couponInput}${invalidInputStyles}`}
            type="text"
            name="couponCode"
            value={coupon}
            disabled={loading}
            onChange={handleOnChange}
            placeholder={placeholder ?? PLACEHOLDER}
            onBlur={handleInputBlur}
            onKeyDown={onKeyDown}
          />
          <Button
            onClick={handleSubmit}
            disabled={loading}
            variation={isDefault ? 'primary-outline' : 'primary'}
          >
            {isDefault ? CTA_APPLY_COUPON : <Spinner />}
          </Button>
        </div>
        {shouldShowCouponText && (
          <p className={styles.successMessage}>{generateCouponText(couponData)}</p>
        )}
        {error && <FieldError errorMsg="This promo code is invalid" />}
      </label>
    </div>
  );
};

export default UniversalCouponInput;
